import React, { Component } from 'react'
import { Line } from 'react-chartjs-2'
import { injectIntl } from 'react-intl'
import moment from 'moment'
import { connect } from 'react-redux'
import { DEFAULT_CHART_COLOR_RGBA } from 'constants/insight'
import { getChartsRatio } from 'helpers/slaMonitor'
import {
  setListForecastData,
  resetChartData,
} from 'reducers/environmentMonitor/reducer'
import { getMaxRangeLimit, getMinRangeLimit } from 'helpers/environmentMonitor'
import { mathRound } from 'helpers/math'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import { GREY, SKY_BLUE, WHITE } from 'constants/colors'
import messages from '../translate'

class ChartStv extends Component {
  constructor(props) {
    super(props)
    this.state = {
      datasetIndex: this.props.datasetIndex,
      itemIndex: 0,
    }
  }

  componentWillUnmount() {
    this.props.resetChartData()
  }

  componentDidUpdate(prevProps) {
    const { chartData, setListForecastData } = this.props
    const { datasetIndex, itemIndex } = this.state

    if (prevProps.chartData !== chartData) {
      const data = chartData.get('data').toJS()

      if (data.length > 1) {
        const item = data[datasetIndex].data[itemIndex]

        setListForecastData({
          time: item ? moment(item.x) : moment(),
          value: item ? mathRound(item.y) : 0,
        })
      } else if (data.length === 1) {
        const item = data[0].data[itemIndex]
        this.props.setListForecastData({
          time: item ? moment(item.x) : moment(),
          value: item ? mathRound(item.y) : 0,
        })
      }
    }
  }

  onChartClick = evt => {
    if (evt.length > 0 && evt[0]._datasetIndex !== undefined) {
      const datasetIndex = evt[0]._datasetIndex
      const itemIndex = evt[0]._index
      const data = this.props.chartData.get('data').toJS()
      const item = data[datasetIndex].data[itemIndex]

      this.setState({ datasetIndex, itemIndex })
      this.props.setListForecastData({
        time: moment(item.x),
        value: Math.round(item.y * 100) / 100,
      })
    }
  }

  repeatDataset = () => {
    const { datasetIndex, itemIndex } = this.state
    const { lineLevels } = this.props
    const chartData = this.props.chartData.toJS()
    let datasets = []

    if (chartData !== undefined && chartData.data !== undefined) {
      datasets = chartData.data.map((dataItem, index) => ({
        label: dataItem.label,
        fill: '-1',
        backgroundColor: SKY_BLUE,
        borderColor:
          index !== 1 && chartData.data.length > 1
            ? WHITE
            : DEFAULT_CHART_COLOR_RGBA,
        pointRadius:
          index === datasetIndex
            ? dataItem.data.map((e, i) => (i === itemIndex ? 5 : 0))
            : 0,
        pointHoverRadius: index === datasetIndex ? 10 : 0,
        pointHitRadius: 30,
        pointBackgroundColor:
          index !== 1 && chartData.data.length > 1
            ? WHITE
            : DEFAULT_CHART_COLOR_RGBA,
        data: dataItem.data,
      }))
    }

    if (lineLevels && lineLevels.line) {
      datasets.push({
        type: 'line',
        label: 'line',
        fill: false,
        tooltip: false,
        pointRadius: 0,
        pointHitRadius: 0,
        pointBorderWidth: 0,
        pointHoverRadius: 0,
        borderDash: [2, 7],
        borderColor: GREY,
        offset: false,
        data: datasets[datasets.length - 1].data.map(item => ({
          x: item.x,
          y: lineLevels.level,
        })),
      })
    }

    return datasets
  }

  chartData = () => ({
    datasets: this.repeatDataset(),
  })

  chartOptions = () => {
    const {
      intl: { formatMessage },
      unit,
      chartData,
    } = this.props

    return {
      aspectRatio: getChartsRatio(),
      responsive: true,
      legend: {
        display: true,
        onClick: e => e.stopPropagation(),
        labels: {
          boxWidth: 20,
          filter(legendItem) {
            if (
              legendItem.datasetIndex !== 0 &&
              legendItem.datasetIndex !== 3 &&
              legendItem.text !== 'line'
            ) {
              return legendItem
            }
          },
        },
      },
      elements: {
        line: {
          tension: 0,
        },
      },
      tooltips: {
        mode: 'x',
        intersect: false,
        position: 'average',
        itemSort(a, b) {
          return a.value - b.value
        },
        callbacks: {
          title: e =>
            `${formatMessage(messages.today).toUpperCase()} - ${moment(
              e[0].label,
            ).format('HH:mm')}`,
          label: (tooltipItem, data) => {
            const dataset = data.datasets[tooltipItem.datasetIndex]
            const item = dataset.data[tooltipItem.index]
            const value = Math.round(item.y * 100) / 100

            if (dataset.label === 'line') {
              return null
            }

            if (
              dataset.label === 'Tide' ||
              dataset.label === 'Cloud Cover' ||
              tooltipItem.datasetIndex === 1
            ) {
              return `${dataset.label}: ${value}`
            }
            if (tooltipItem.datasetIndex > 1) {
              const kits = data.datasets
              const itemFirst = kits[0].data[tooltipItem.index]
              const itemSecond =
                kits[kits.length - 1].label !== 'line'
                  ? kits[kits.length - 1].data[tooltipItem.index]
                  : kits[kits.length - 2].data[tooltipItem.index]

              return `95% CI: [${Math.round(itemSecond.y * 100) / 100}, ${
                Math.round(itemFirst.y * 100) / 100
              }]`
            }
          },
        },
      },
      scales: {
        xAxes: [
          {
            type: 'time',
            distribution: 'series',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'HH:mm',
              },
            },
            scaleLabel: {
              display: true,
              labelString: formatMessage(messages.hour),
            },
            ticks: {
              beginAtZero: false,
              minRotation: ifWindowWidthLessThan(992) ? 30 : 0,
            },
            gridLines: {
              drawOnChartArea: false,
            },
          },
        ],
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
              max: getMaxRangeLimit(chartData),
              min: getMinRangeLimit(chartData),
            },
            scaleLabel: {
              display: true,
              labelString: unit,
            },
          },
        ],
      },
      plugins: {
        datalabels: {
          display: false,
        },
      },
    }
  }

  render() {
    return (
      <Line
        width={null}
        height={null}
        data={this.chartData()}
        options={this.chartOptions()}
        getElementAtEvent={this.onChartClick}
      />
    )
  }
}

export default connect(null, {
  setListForecastData,
  resetChartData,
})(injectIntl(ChartStv))
